import React, { useState, useEffect } from 'react';
import api from '../axiosConfig';
import Sidebar from './Sidebar';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Clock, FileText, Search, QrCode, Loader2, FileSpreadsheet, Zap, PenTool, X, Upload, RotateCcw } from 'lucide-react';

const LiberacionesAdmin = () => {
  const navigate = useNavigate();
  const [alumnos, setAlumnos] = useState([]);
  const [alumnosPendientes, setAlumnosPendientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPendientes, setLoadingPendientes] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [periodos, setPeriodos] = useState([]);
  const [selectedPeriodo, setSelectedPeriodo] = useState('ALL');
  const [periodoActivoLabel, setPeriodoActivoLabel] = useState(null);
  const [selectedPrograma, setSelectedPrograma] = useState('ALL');
  const [selectedEstado, setSelectedEstado] = useState('ALL');
  const PAGE_SIZE = 50;
  const [pageListos, setPageListos] = useState(1);
  const [pagePendientes, setPagePendientes] = useState(1);
  const [generatingId, setGeneratingId] = useState(null);
  const [revokingId, setRevokingId] = useState(null);
  const [isBulkLoading, setIsBulkLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [firmaBase64, setFirmaBase64] = useState(null);
  const [isSavingFirma, setIsSavingFirma] = useState(false);

  const fetchAlumnosCompletos = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/liberaciones/alumnos-completos');
      setAlumnos(data);
    } catch (err) {
      setError('Error al cargar la lista de alumnos');
    } finally {
      setLoading(false);
    }
  };

  const fetchAlumnosPendientes = async () => {
    try {
      setLoadingPendientes(true);
      const { data } = await api.get('/liberaciones/alumnos-pendientes');
      setAlumnosPendientes(data);
    } catch (err) {
      console.error('Error al cargar alumnos pendientes:', err);
    } finally {
      setLoadingPendientes(false);
    }
  };

  const fetchFirma = async () => {
    try {
      const { data } = await api.get('/users/perfil/firma/obtener');
      setFirmaBase64(data.firma_digital);
    } catch (err) {
      console.error('Error al cargar firma');
    }
  };

  const fetchPeriodos = async () => {
    try {
      const { data } = await api.get('/periodos');
      setPeriodos(Array.isArray(data) ? data : []);
    } catch (err) {
      setPeriodos([]);
    }
  };

  useEffect(() => {
    fetchAlumnosCompletos();
    fetchAlumnosPendientes();
    fetchFirma();
    fetchPeriodos();

    const loadPeriodoActivo = async () => {
      try {
        const { data } = await api.get('/periodos/activo');
        const label = formatPeriodoLabel(data);
        setPeriodoActivoLabel(label || null);
      } catch (err) {
        setPeriodoActivoLabel(null);
      }
    };

    loadPeriodoActivo();
  }, []);

  const formatPeriodoLabel = (periodo) => {
    const fase = periodo?.Fase ?? '';
    const anio = periodo?.Año ?? '';
    return `${fase} ${anio}`.trim();
  };

  const normalizePeriodoValue = (raw) => (raw && String(raw).trim()) || 'SIN PERIODO';
  const normalizeProgramaValue = (raw) => (raw && String(raw).trim()) || 'SIN PROGRAMA';

  const getEstadoToken = (alumno) => {
    if (alumno?.ya_liberado) return 'LIBERADO';
    const raw = (alumno?.estado_liberacion ?? '').toString().trim().toUpperCase();
    if (raw === 'EN ESPERA' || raw === 'ESPERA') return 'ESPERA';
    if (raw === 'PENDIENTE') return 'PENDIENTE';
    return 'PENDIENTE';
  };

  const handleCambiarEstadoEspera = async (alumno) => {
    const nuevoEstado = alumno.estado_liberacion === 'En Espera' ? 'Pendiente' : 'En Espera';
    try {
      await api.put(`/liberaciones/cambiar-estado-espera/${alumno.id_proceso}`, { estado: nuevoEstado });
      fetchAlumnosPendientes();
    } catch (err) {
      alert('Error al cambiar el estado de espera');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          // Crear un canvas para redimensionar la imagen
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 1000;
          const MAX_HEIGHT = 1000;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Convertir a base64 con calidad optimizada
          const optimizedBase64 = canvas.toDataURL('image/png', 0.7);
          setFirmaBase64(optimizedBase64);
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGuardarFirma = async () => {
    try {
      setIsSavingFirma(true);
      await api.put('/users/perfil/firma', { firma_digital: firmaBase64 });
      alert('Firma personal guardada correctamente');
      setShowSignatureModal(false);
    } catch (err) {
      alert('Error al guardar la firma');
    } finally {
      setIsSavingFirma(false);
    }
  };

  const handleLiberar = async (proceso) => {
    try {
      setGeneratingId(proceso.id_proceso);
      await api.get(`/liberaciones/generar-pdf/${proceso.id_proceso}`);
      await fetchAlumnosCompletos();
      await fetchAlumnosPendientes();
      alert(`Liberación generada con éxito para ${proceso.nombre_estudiante}`);
    } catch (err) {
      alert('Error al generar la liberación');
    } finally {
      setGeneratingId(null);
    }
  };

  const handleLiberarMasivo = async () => {
    const alumnosPendientesLib = filteredAlumnos.filter((a) => !a.ya_liberado);
    if (alumnosPendientesLib.length === 0) {
      alert('No hay alumnos pendientes por liberar');
      return;
    }

    if (!window.confirm(`¿Estás seguro de liberar a los ${alumnosPendientesLib.length} alumnos pendientes?`)) {
      return;
    }

    try {
      setIsBulkLoading(true);
      const ids_proceso = alumnosPendientesLib.map(a => a.id_proceso);
      await api.post('/liberaciones/generar-masivo', { ids_proceso });
      await fetchAlumnosCompletos();
      await fetchAlumnosPendientes();
      alert('Liberación masiva completada con éxito');
    } catch (err) {
      alert('Error en la liberación masiva');
    } finally {
      setIsBulkLoading(false);
    }
  };

  const handleExportarExcel = async () => {
    try {
      setIsExporting(true);
      const response = await api.get('/liberaciones/exportar-excel', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Alumnos_Liberados.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Error al exportar el archivo Excel');
    } finally {
      setIsExporting(false);
    }
  };

  const buildCodigoFormateado = (proceso) => {
    const raw = proceso?.codigo_unico || '';
    const codigo = raw.toString().trim().toUpperCase();
    return codigo ? `LIB-${codigo}` : null;
  };

  const handleVisualizarPDF = (proceso) => {
    const codigo = buildCodigoFormateado(proceso);
    if (!codigo) {
      alert('No se encontró el código de liberación para este alumno');
      return;
    }
    navigate(`/verificar-liberacion?codigo=${encodeURIComponent(codigo)}`);
  };

  const handleDescargarPDF = async (proceso) => {
    try {
      setGeneratingId(proceso.id_proceso);
      const codigo = buildCodigoFormateado(proceso);
      if (!codigo) {
        alert('No se encontró el código de liberación para este alumno');
        return;
      }

      const response = await api.get(`/liberaciones/pdf/${encodeURIComponent(codigo)}`, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Liberacion-${proceso.matricula}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error al descargar el PDF');
    } finally {
      setGeneratingId(null);
    }
  };

  const handleRevertirLiberacion = async (alumno) => {
    if (!alumno?.liberacion_id) {
      alert('No se encontró el ID de liberación');
      return;
    }

    const ok = window.confirm(
      `¿Deseas revertir la liberación del alumno ${alumno.matricula} (${alumno.tipo_proceso})?\n\nEsto eliminará el código LIB y podrás generarlo nuevamente.`
    );
    if (!ok) return;

    try {
      setRevokingId(alumno.id_proceso);
      await api.delete(`/liberaciones/revertir/${alumno.liberacion_id}`);
      await fetchAlumnosCompletos();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al revertir la liberación');
    } finally {
      setRevokingId(null);
    }
  };

  const matchesPeriodo = (alumno) => {
    const alumnoPeriodo = normalizePeriodoValue(alumno?.periodo);
    if (selectedPeriodo === 'ALL') return true;
    if (selectedPeriodo === 'SIN') return alumnoPeriodo === 'SIN PERIODO';
    return alumnoPeriodo === selectedPeriodo;
  };

  useEffect(() => {
    if (selectedPeriodo !== 'ALL') return;
    if (!periodoActivoLabel) return;
    setSelectedPeriodo(periodoActivoLabel);
  }, [periodoActivoLabel, selectedPeriodo]);

  const matchesPrograma = (alumno) => {
    const alumnoPrograma = normalizeProgramaValue(alumno?.programa_educativo);
    if (selectedPrograma === 'ALL') return true;
    if (selectedPrograma === 'SIN') return alumnoPrograma === 'SIN PROGRAMA';
    return alumnoPrograma === selectedPrograma;
  };

  const matchesEstado = (alumno) => {
    if (selectedEstado === 'ALL') return true;
    return getEstadoToken(alumno) === selectedEstado;
  };

  const programas = Array.from(
    new Set(
      [...alumnos, ...alumnosPendientes]
        .map((a) => normalizeProgramaValue(a?.programa_educativo))
        .filter((p) => p && p !== 'SIN PROGRAMA')
    )
  ).sort((a, b) => a.localeCompare(b));

  const filteredAlumnos = alumnos.filter((alumno) => {
    if (!matchesPeriodo(alumno)) return false;
    if (!matchesPrograma(alumno)) return false;
    if (!matchesEstado(alumno)) return false;
    const search = searchTerm.toLowerCase();
    const nombre = (alumno?.nombre_estudiante || '').toLowerCase();
    const matricula = (alumno?.matricula || '').toLowerCase();
    return nombre.includes(search) || matricula.includes(search);
  });

  const filteredPendientes = alumnosPendientes.filter((alumno) => {
    if (!matchesPeriodo(alumno)) return false;
    if (!matchesPrograma(alumno)) return false;
    if (!matchesEstado(alumno)) return false;
    const search = searchTerm.toLowerCase();
    const nombre = (alumno?.nombre_completo || alumno?.nombre_estudiante || '').toLowerCase();
    const matricula = (alumno?.matricula || '').toLowerCase();
    return nombre.includes(search) || matricula.includes(search);
  });

  useEffect(() => {
    setPageListos(1);
    setPagePendientes(1);
  }, [searchTerm, selectedPeriodo, selectedPrograma, selectedEstado]);

  const totalPagesListos = Math.max(1, Math.ceil(filteredAlumnos.length / PAGE_SIZE));
  const totalPagesPendientes = Math.max(1, Math.ceil(filteredPendientes.length / PAGE_SIZE));

  useEffect(() => {
    if (pageListos > totalPagesListos) setPageListos(totalPagesListos);
  }, [pageListos, totalPagesListos]);

  useEffect(() => {
    if (pagePendientes > totalPagesPendientes) setPagePendientes(totalPagesPendientes);
  }, [pagePendientes, totalPagesPendientes]);

  const paginatedAlumnos = filteredAlumnos.slice((pageListos - 1) * PAGE_SIZE, pageListos * PAGE_SIZE);
  const paginatedPendientes = filteredPendientes.slice((pagePendientes - 1) * PAGE_SIZE, pagePendientes * PAGE_SIZE);

  const getPaginationItems = (totalPages, currentPage) => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const items = [1];
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    if (start > 2) items.push('…');
    for (let p = start; p <= end; p += 1) items.push(p);
    if (end < totalPages - 1) items.push('…');
    items.push(totalPages);

    return items;
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <Sidebar />
      <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-8 overflow-x-hidden">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
              <CheckCircle className="text-green-600" size={32} />
              Liberación de Documentación
            </h2>
            <p className="text-gray-600 mt-2">
              Alumnos que han completado satisfactoriamente toda su documentación.
            </p>
          </motion.div>

          {/* Buscador y Botones de Acción */}
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-6 flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[300px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Buscar por nombre o matrícula..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="min-w-[220px]">
              <select
                value={selectedPeriodo}
                onChange={(e) => setSelectedPeriodo(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all"
              >
                <option value="ALL">Todos los periodos</option>
                {periodoActivoLabel ? (
                  <option value={periodoActivoLabel}>
                    Periodo actual: {periodoActivoLabel}
                  </option>
                ) : null}
                {periodos.map((p) => {
                  const label = formatPeriodoLabel(p);
                  if (!label) return null;
                  if (label === periodoActivoLabel) return null;
                  return (
                    <option key={p.IdPeriodo} value={label}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="min-w-[240px]">
              <select
                value={selectedPrograma}
                onChange={(e) => setSelectedPrograma(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all"
              >
                <option value="ALL">Todos los programas</option>
                {programas.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>

            <div className="min-w-[210px]">
              <select
                value={selectedEstado}
                onChange={(e) => setSelectedEstado(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all"
              >
                <option value="ALL">Todos los estados</option>
                <option value="LIBERADO">Liberado</option>
                <option value="PENDIENTE">Pendiente</option>
                <option value="ESPERA">En espera</option>
              </select>
            </div>
            
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setShowSignatureModal(true)}
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-all text-sm font-bold shadow-sm"
              >
                <PenTool size={18} />
                Firma Personal
              </button>

              <button 
                onClick={handleLiberarMasivo}
                disabled={isBulkLoading || loading || filteredAlumnos.filter((a) => !a.ya_liberado).length === 0}
                className="flex items-center gap-2 bg-gray-900 hover:bg-black text-white px-4 py-2 rounded-lg transition-all disabled:opacity-50 text-sm font-bold"
              >
                {isBulkLoading ? <Loader2 size={18} className="animate-spin" /> : <Zap size={18} className="text-yellow-400" />}
                Liberar Todo 
              </button>

              <button 
                onClick={handleExportarExcel}
                disabled={isExporting || loading || alumnos.length === 0}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-all disabled:opacity-50 text-sm font-bold"
              >
                {isExporting ? <Loader2 size={18} className="animate-spin" /> : <FileSpreadsheet size={18} />}
                Exportar Excel
              </button>

              <button 
                onClick={fetchAlumnosCompletos}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors text-sm"
              >
                Actualizar
              </button>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-xl font-bold text-gray-700 flex items-center gap-2 mb-4">
              <CheckCircle className="text-green-500" size={20} />
              Listos para Liberación
            </h3>
            {loading ? (
              <div className="flex flex-col items-center justify-center py-10">
                <Loader2 className="animate-spin text-red-600 mb-4" size={32} />
                <p className="text-gray-500 font-medium text-sm">Cargando alumnos completados...</p>
              </div>
            ) : error ? (
              <div className="bg-red-50 text-red-600 p-6 rounded-xl border border-red-100 text-center">
                {error}
              </div>
            ) : filteredAlumnos.length === 0 ? (
              <div className="bg-white p-8 rounded-xl border-2 border-dashed border-gray-200 text-center">
                <Clock className="mx-auto text-gray-300 mb-4" size={48} />
                <h3 className="text-lg font-bold text-gray-700">No hay alumnos para liberar</h3>
                <p className="text-gray-500 text-sm mt-1">Los alumnos aparecerán aquí automáticamente cuando todos sus documentos sean aprobados.</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 table-fixed">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-[120px]">Proceso</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-[240px]">Nombre</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-[140px]">Matrícula</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-[260px]">Programa</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-[180px]">Periodo</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider w-[240px]">Estado</th>
                        <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider w-[240px]">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {paginatedAlumnos.map((alumno) => (
                        <tr key={alumno.id_proceso} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-4 align-middle">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-black uppercase bg-red-100 text-red-700">
                              {alumno.tipo_proceso}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-800 font-semibold align-middle">
                            <span className="block truncate" title={alumno.nombre_estudiante}>
                              {alumno.nombre_estudiante}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600 font-mono whitespace-nowrap align-middle">
                            {alumno.matricula}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600 align-middle">
                            <div className="flex items-center gap-2 min-w-0">
                              <FileText size={14} className="text-gray-300" />
                              <span className="truncate">{alumno.programa_educativo}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600 align-middle">
                            <div className="flex items-center gap-2 min-w-0">
                              <Clock size={14} className="text-gray-300" />
                              <span className="truncate">{alumno.periodo}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4 align-middle">
                            {alumno.ya_liberado ? (
                              <div className="inline-flex items-center gap-2 bg-green-50 border border-green-100 px-3 py-2 rounded-lg max-w-full">
                                <QrCode className="text-green-600" size={16} />
                                <div className="min-w-0">
                                  <div className="text-xs font-bold text-green-700 leading-tight">Liberado</div>
                                  <div className="text-[11px] font-mono font-black text-green-800 truncate max-w-[160px]" title={alumno.codigo_unico}>
                                    {alumno.codigo_unico}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <span className="inline-flex items-center gap-2 bg-yellow-50 border border-yellow-100 px-2.5 py-1.5 rounded-lg">
                                <Clock className="text-yellow-600" size={16} />
                                <span className="text-xs font-bold text-yellow-700">Pendiente</span>
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-4 align-middle">
                            <div className="flex items-center justify-end gap-2 flex-nowrap">
                              {alumno.ya_liberado ? (
                                <>
                                  <button
                                    onClick={() => handleVisualizarPDF(alumno)}
                                    disabled={generatingId === alumno.id_proceso || revokingId === alumno.id_proceso}
                                    aria-label="Ver PDF"
                                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-3 py-2 rounded-lg transition-all flex items-center justify-center gap-2 text-xs disabled:opacity-50 whitespace-nowrap"
                                  >
                                    {generatingId === alumno.id_proceso ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                                    <span className="hidden sm:inline">Ver</span>
                                  </button>
                                  <button
                                    onClick={() => handleDescargarPDF(alumno)}
                                    disabled={generatingId === alumno.id_proceso || revokingId === alumno.id_proceso}
                                    aria-label="Descargar PDF"
                                    className="bg-red-700 hover:bg-red-800 text-white font-bold px-3 py-2 rounded-lg transition-all flex items-center justify-center gap-2 text-xs disabled:opacity-50 whitespace-nowrap"
                                  >
                                    {generatingId === alumno.id_proceso ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                                    <span className="hidden sm:inline">PDF</span>
                                  </button>
                                  <button
                                    onClick={() => handleRevertirLiberacion(alumno)}
                                    disabled={generatingId === alumno.id_proceso || revokingId === alumno.id_proceso}
                                    aria-label="Deshacer liberación"
                                    className="bg-gray-200 hover:bg-gray-300 text-gray-900 font-bold px-3 py-2 rounded-lg transition-all flex items-center justify-center gap-2 text-xs disabled:opacity-50 whitespace-nowrap"
                                  >
                                    {revokingId === alumno.id_proceso ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                                    <span className="hidden sm:inline">Deshacer</span>
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => handleLiberar(alumno)}
                                  disabled={generatingId === alumno.id_proceso}
                                  aria-label="Generar liberación"
                                  className="bg-gray-900 hover:bg-black text-white font-bold px-3 py-2 rounded-lg transition-all flex items-center justify-center gap-2 text-xs disabled:opacity-50 whitespace-nowrap"
                                >
                                  {generatingId === alumno.id_proceso ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                                  <span className="hidden sm:inline">Generar</span>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-4 py-4 border-t border-gray-100">
                  <div className="text-sm text-gray-500 mb-3">
                    Mostrando {paginatedAlumnos.length} de {filteredAlumnos.length} registros
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <button
                      onClick={() => setPageListos((p) => Math.max(1, p - 1))}
                      disabled={pageListos <= 1}
                      className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                        pageListos <= 1
                          ? 'bg-gray-100 text-gray-400'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Anterior
                    </button>

                    <div className="flex items-center gap-2">
                      {getPaginationItems(totalPagesListos, pageListos).map((item, index) => {
                        if (item === '…') {
                          return (
                            <span key={`l-ellipsis-${index}`} className="px-2 text-gray-400">
                              …
                            </span>
                          );
                        }

                        const page = item;
                        const isActive = page === pageListos;
                        return (
                          <button
                            key={`l-page-${page}`}
                            onClick={() => setPageListos(page)}
                            className={`w-10 h-10 rounded-lg text-sm font-bold transition-colors ${
                              isActive ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => setPageListos((p) => Math.min(totalPagesListos, p + 1))}
                      disabled={pageListos >= totalPagesListos}
                      className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                        pageListos >= totalPagesListos
                          ? 'bg-gray-100 text-gray-400'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <hr className="my-12 border-gray-200" />

          {/* Sección de Alumnos Pendientes */}
          <div>
            <h3 className="text-xl font-bold text-gray-700 flex items-center gap-2 mb-4">
              <Clock className="text-orange-500" size={20} />
              Alumnos con Trámite Pendiente
            </h3>
            <p className="text-gray-500 text-sm mb-6">
              Estudiantes que han iniciado su proceso pero aún no completan toda la documentación.
            </p>

            {loadingPendientes ? (
              <div className="flex flex-col items-center justify-center py-10">
                <Loader2 className="animate-spin text-orange-600 mb-4" size={32} />
                <p className="text-gray-500 font-medium text-sm">Cargando trámites pendientes...</p>
              </div>
            ) : filteredPendientes.length === 0 ? (
              <div className="bg-white p-8 rounded-xl border-2 border-dashed border-gray-200 text-center">
                <Clock className="mx-auto text-gray-300 mb-4" size={48} />
                <h3 className="text-lg font-bold text-gray-700">No hay trámites pendientes</h3>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {paginatedPendientes.map((alumno) => (
                    <motion.div
                      key={alumno.id_proceso}
                      layout
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className={`bg-white rounded-2xl shadow-sm border overflow-hidden hover:shadow-md transition-all ${
                        alumno.estado_liberacion === 'En Espera' ? 'border-orange-300 ring-2 ring-orange-50' : 'border-gray-100'
                      }`}
                    >
                      <div className={`${alumno.estado_liberacion === 'En Espera' ? 'bg-orange-600' : 'bg-gray-700'} p-4 text-white relative`}>
                        <div className="flex justify-between items-start">
                          <span className="bg-black/20 text-[10px] uppercase font-bold px-2 py-1 rounded-full">
                            {alumno.tipo_proceso}
                          </span>
                          {alumno.estado_liberacion === 'En Espera' && (
                            <span className="bg-white text-orange-600 text-[10px] uppercase font-black px-2 py-1 rounded-full shadow-sm">
                              EN ESPERA
                            </span>
                          )}
                        </div>
                        <h3 className="font-bold text-lg mt-2 truncate">{alumno.nombre_completo}</h3>
                        <p className="text-white/70 text-sm">{alumno.matricula}</p>
                      </div>

                      <div className="p-5">
                        <div className="mb-4">
                          <div className="flex justify-between items-end mb-1">
                            <span className="text-xs font-bold text-gray-500 uppercase">Progreso de Documentos</span>
                            <span className="text-xs font-black text-gray-700">{alumno.progreso.porcentaje}%</span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                            <motion.div 
                              initial={{ width: 0 }}
                              animate={{ width: `${alumno.progreso.porcentaje}%` }}
                              className={`h-full transition-all duration-500 ${
                                alumno.progreso.porcentaje > 80 ? 'bg-green-500' : 
                                alumno.progreso.porcentaje > 40 ? 'bg-blue-500' : 'bg-orange-500'
                              }`}
                            />
                          </div>
                          <p className="text-[10px] text-gray-400 mt-1">
                            {alumno.progreso.aprobados} de {alumno.progreso.total} documentos aprobados
                          </p>
                        </div>

                        <div className="space-y-2 mb-6">
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <FileText size={14} className="text-gray-300" />
                            <span className="truncate">{alumno.programa_educativo}</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Clock size={14} className="text-gray-300" />
                            <span>{alumno.periodo}</span>
                          </div>
                        </div>

                        <button
                          onClick={() => handleCambiarEstadoEspera(alumno)}
                          className={`w-full py-2.5 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                            alumno.estado_liberacion === 'En Espera' 
                              ? 'bg-orange-100 text-orange-700 hover:bg-orange-200 border border-orange-200'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                          }`}
                        >
                          {alumno.estado_liberacion === 'En Espera' ? (
                            <>
                              <Clock size={16} />
                              Quitar de Espera
                            </>
                          ) : (
                            <>
                              <Clock size={16} />
                              Marcar en Espera
                            </>
                          )}
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="bg-white rounded-xl border border-gray-100 px-4 py-4">
                  <div className="text-sm text-gray-500 mb-3">
                    Mostrando {paginatedPendientes.length} de {filteredPendientes.length} registros
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <button
                      onClick={() => setPagePendientes((p) => Math.max(1, p - 1))}
                      disabled={pagePendientes <= 1}
                      className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                        pagePendientes <= 1
                          ? 'bg-gray-100 text-gray-400'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Anterior
                    </button>

                    <div className="flex items-center gap-2">
                      {getPaginationItems(totalPagesPendientes, pagePendientes).map((item, index) => {
                        if (item === '…') {
                          return (
                            <span key={`p-ellipsis-${index}`} className="px-2 text-gray-400">
                              …
                            </span>
                          );
                        }

                        const page = item;
                        const isActive = page === pagePendientes;
                        return (
                          <button
                            key={`p-page-${page}`}
                            onClick={() => setPagePendientes(page)}
                            className={`w-10 h-10 rounded-lg text-sm font-bold transition-colors ${
                              isActive ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => setPagePendientes((p) => Math.min(totalPagesPendientes, p + 1))}
                      disabled={pagePendientes >= totalPagesPendientes}
                      className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                        pagePendientes >= totalPagesPendientes
                          ? 'bg-gray-100 text-gray-400'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Firma Personal */}
      {showSignatureModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
          >
            <div className="bg-indigo-600 p-4 text-white flex justify-between items-center">
              <div className="flex items-center gap-2">
                <PenTool size={20} />
                <h3 className="font-bold">Configurar Firma Personal</h3>
              </div>
              <button onClick={() => setShowSignatureModal(false)} className="hover:bg-white/20 p-1 rounded-full transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 text-center">
              <p className="text-gray-600 text-sm mb-6">
                Sube una imagen de tu firma (formato PNG con fondo transparente recomendado) para que aparezca en los documentos de liberación.
              </p>

              <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 mb-6 relative hover:border-indigo-400 transition-colors group">
                {firmaBase64 ? (
                  <div className="relative inline-block">
                    <img src={firmaBase64} alt="Previsualización de firma" className="max-h-32 mx-auto" />
                    <button 
                      onClick={() => setFirmaBase64(null)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full shadow-lg hover:bg-red-600 transition-colors"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <label className="cursor-pointer flex flex-col items-center gap-2">
                    <Upload className="text-gray-400 group-hover:text-indigo-500 transition-colors" size={40} />
                    <span className="text-sm text-gray-500 font-medium">Hacer clic para subir imagen</span>
                    <input type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
                  </label>
                )}
              </div>

              <div className="flex gap-3 mt-8">
                <button
                  onClick={() => setShowSignatureModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleGuardarFirma}
                  disabled={!firmaBase64 || isSavingFirma}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors font-bold shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSavingFirma ? <Loader2 size={18} className="animate-spin" /> : 'Guardar Firma'}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default LiberacionesAdmin;
